import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Settings2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

export default function AuditLogPanel() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("");
  const [page, setPage] = useState(0);

  const [retentionDays, setRetentionDays] = useState<number>(90);
  const [savedRetentionDays, setSavedRetentionDays] = useState<number>(90);
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => { loadRetentionConfig(); }, []);
  useEffect(() => { setPage(0); }, [entityTypeFilter, eventTypeFilter]);
  useEffect(() => { loadLogs(); }, [entityTypeFilter, eventTypeFilter, page]);

  async function loadRetentionConfig() {
    const { data } = await supabase
      .from("matching_config")
      .select("config_value")
      .eq("config_key", "audit_retention_days")
      .maybeSingle();
    if (data?.config_value) {
      const days = typeof data.config_value === "number" ? data.config_value : Number(data.config_value);
      if (!isNaN(days) && days > 0) {
        setRetentionDays(days);
        setSavedRetentionDays(days);
      }
    }
  }

  async function saveRetentionConfig() {
    setSavingConfig(true);
    const { data: existing } = await supabase
      .from("matching_config")
      .select("id")
      .eq("config_key", "audit_retention_days")
      .maybeSingle();

    if (existing) {
      await supabase.from("matching_config").update({ config_value: retentionDays as any }).eq("config_key", "audit_retention_days");
    } else {
      await supabase.from("matching_config").insert({ config_key: "audit_retention_days", config_value: retentionDays as any });
    }
    setSavedRetentionDays(retentionDays);
    setSavingConfig(false);
    toast.success(t("op.auditSaved", { days: retentionDays }));
  }

  async function purgeOldLogs() {
    if (!confirm(t("op.auditPurgeConfirm", { days: savedRetentionDays }))) return;
    setPurging(true);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - savedRetentionDays);
    const { error } = await supabase
      .from("audit_log")
      .delete()
      .lt("created_at", cutoff.toISOString());
    setPurging(false);
    if (error) {
      toast.error(t("common.error") + ": " + error.message);
    } else {
      toast.success(t("op.auditPurged"));
      loadLogs();
    }
  }

  async function loadLogs() {
    setLoading(true);
    let countQuery = supabase.from("audit_log").select("*", { count: "exact", head: true });
    if (entityTypeFilter) countQuery = countQuery.eq("entity_type", entityTypeFilter);
    if (eventTypeFilter) countQuery = countQuery.eq("event_type", eventTypeFilter);
    const { count } = await countQuery;
    setTotalCount(count || 0);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    let query = supabase.from("audit_log").select("*").order("created_at", { ascending: false }).range(from, to);
    if (entityTypeFilter) query = query.eq("entity_type", entityTypeFilter);
    if (eventTypeFilter) query = query.eq("event_type", eventTypeFilter);
    const { data } = await query;
    setLogs(data || []);
    setLoading(false);
  }

  const entityTypes = [...new Set(logs.map((l) => l.entity_type))].sort();
  const eventTypes = [...new Set(logs.map((l) => l.event_type))].sort();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  function exportCSV() {
    const header = `${t("op.colTimestamp")},${t("op.colEvent")},${t("op.colEntity")},Entity-ID,${t("op.colActor")},${t("op.colMeta")}\n`;
    const rows = logs.map((l) =>
      `"${new Date(l.created_at).toISOString()}","${l.event_type}","${l.entity_type}","${l.entity_id || ""}","${l.actor_role || ""}","${JSON.stringify(l.meta || {}).replace(/"/g, '""')}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-2">
          <select value={entityTypeFilter} onChange={(e) => setEntityTypeFilter(e.target.value)}
            className="h-8 px-3 border border-border bg-background text-xs focus:outline-none focus:border-primary">
            <option value="">{t("op.auditAllEntities")}</option>
            {entityTypes.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
          </select>
          <select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}
            className="h-8 px-3 border border-border bg-background text-xs focus:outline-none focus:border-primary">
            <option value="">{t("op.auditAllEvents")}</option>
            {eventTypes.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowConfig(!showConfig)}
            className={cn("text-xs px-3 py-1.5 border border-border transition-colors flex items-center gap-1.5",
              showConfig ? "text-foreground border-foreground" : "text-muted-foreground hover:text-foreground")}>
            <Settings2 className="h-3.5 w-3.5" />
            {t("op.auditRetention")}
          </button>
          <button onClick={exportCSV} className="text-xs px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground transition-colors">
            {t("op.auditCSV")}
          </button>
        </div>
      </div>

      {/* Retention config panel */}
      {showConfig && (
        <div className="border border-border bg-card p-4 space-y-3">
          <p className="text-xs text-muted-foreground">{t("op.auditRetentionLabel")}</p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={7}
              max={3650}
              value={retentionDays}
              onChange={(e) => setRetentionDays(Math.max(7, parseInt(e.target.value) || 7))}
              className="h-8 w-24 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary"
            />
            <span className="text-sm text-muted-foreground">{t("op.auditDays")}</span>
            <button
              onClick={saveRetentionConfig}
              disabled={savingConfig || retentionDays === savedRetentionDays}
              className="text-xs px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
              {savingConfig ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("common.save")}
            </button>
            <button
              onClick={purgeOldLogs}
              disabled={purging}
              className="text-xs px-3 py-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors flex items-center gap-1.5 disabled:opacity-40">
              {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              {t("op.auditPurgeNow")}
            </button>
          </div>
          <p className="text-micro text-muted-foreground">
            {t("op.auditCurrentRetention", { days: savedRetentionDays })}
          </p>
        </div>
      )}

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 text-micro font-normal">{t("op.colTimestamp")}</th>
              <th className="text-left p-3 text-micro font-normal">{t("op.colEvent")}</th>
              <th className="text-left p-3 text-micro font-normal">{t("op.colEntity")}</th>
              <th className="text-left p-3 text-micro font-normal">{t("op.colActor")}</th>
              <th className="text-left p-3 text-micro font-normal">{t("op.colMeta")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-caption">{t("common.loading")}</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-caption">{t("op.billingEmpty")}</td></tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id} className="border-b border-border hover:bg-secondary/20">
                  <td className="p-3 text-micro whitespace-nowrap">{new Date(log.created_at).toLocaleString("de-DE")}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 bg-secondary border border-border">{log.event_type}</span></td>
                  <td className="p-3 text-caption">{log.entity_type}<br/><span className="text-micro">{log.entity_id?.slice(0, 8) || "—"}</span></td>
                  <td className="p-3 text-caption">{log.actor_role || "—"}</td>
                  <td className="p-3 text-micro text-muted-foreground max-w-xs truncate">
                    {log.meta ? JSON.stringify(log.meta).slice(0, 80) : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-micro text-muted-foreground">
            {t("op.auditEntries", { count: totalCount, page: page + 1, total: totalPages })}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="h-8 w-8 flex items-center justify-center border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 7) {
                pageNum = i;
              } else if (page < 3) {
                pageNum = i;
              } else if (page > totalPages - 4) {
                pageNum = totalPages - 7 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cn(
                    "h-8 w-8 flex items-center justify-center text-xs border transition-colors",
                    pageNum === page
                      ? "border-foreground text-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  )}>
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="h-8 w-8 flex items-center justify-center border border-border text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
